import { useState, useEffect, useReducer, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Collapse,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  MenuItem,
  InputAdornment,
  FormHelperText,
  FormControl,
  IconButton,
  Grid,
} from '@material-ui/core';
import ButtonProgress from '../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import Display from '../Display/Display';
import Visible from '../../../../components/Visible/Visible';
import ConfirmationAction from '../../../../eliot-components/src/ConfirmationDialog/ConfirmationDialog';
import { useManageContext } from '../../../../contexts/ManageContext/ManageContext';
import { Props } from './types';
import {
  helpName,
  helpAuthURL,
  helpKey,
  helpLocation,
  helpPassword,
  helpUsername,
  helpCode,
  helpPrivateKey,
  helpPrivateKeyPassword,
  helpFormat,
  helpSelectedAuthType,
  helpRedirectURI,
  helpGrantType,
  helpOrganization,
  helpAllowIP,
} from './helperTexts';
import {
  authTypes,
  orderedAuthTypes,
  initialState,
  editTargetSystemReducer,
  apiFormats,
  stripHttpsFromTheBegining,
  appendHttpsOnTheBegining,
  grantTypes,
  focusedFieldReducer,
  isTargetSystemFromDemoOrg,
  isPrivateKeyEncrypted,
  isCareVisible,
  HL7Formats,
  UnsupportedHL7Devices,
  UnsupportedHL7Types,
} from './provider';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import MedSelect from '../../../../eliot-components/src/Common/MedSelect';
import useStyles from './styles';
import { isInvalid } from './validations';
import { MedisanteTargetSystem } from '../../types';
import { Alert } from '@material-ui/lab';
import {
  useCreateCareTargetSystemMutation,
  useCreateTargetSystemMutation,
  useEditTargetSystemMutation,
} from '../../../../store/api-slice';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../eliot-components/src/utils/helpers';
import { FileCopy } from '@material-ui/icons';

const countCareSystems = (
  targetSystems: MedisanteTargetSystem[],
  orgId: string | null
) =>
  !!orgId && !!targetSystems
    ? targetSystems.filter((ts) => ts.owner === orgId && !ts.isMutable).length
    : null;

const EditTargetSystemDialog = (props: Props) => {
  const { open, onClose, onChange, title, data, targetSystems } = props;
  const [isUseCare, setUseCare] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [showPrivateKeyPassword, setShowPrivateKeyPassword] = useState(false);
  const { state: mState } = useManageContext();
  const { authUser } = useAuthUser();
  const config = useConfig();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedOrgs } = mState;
  const userOrgs =
    selectedOrgs && selectedOrgs.length !== 0
      ? selectedOrgs
      : authUser.orgs
      ? authUser.orgs
      : [];

  const initialOrgs =
    authUser.selectedOrgs.length === 1
      ? authUser.selectedOrgs
      : userOrgs.length === 1
      ? [userOrgs[0].id]
      : [];
  const [organizations, setOrganization] = useState<string[]>(initialOrgs);
  const classes = useStyles();
  const [updateTargetSystem, { isLoading: isLoadingEdit }] =
    useEditTargetSystemMutation();
  const [saveTargetSystem, { isLoading: isLoadingCreate }] =
    useCreateTargetSystemMutation();

  const isLoading = isLoadingCreate || isLoadingEdit;
  const [state, dispatchAction] = useReducer(editTargetSystemReducer, {
    ...initialState,
    organization: organizations,
  });
  const [focusedFieldState, dispatchFocusedField] = useReducer(
    focusedFieldReducer,
    {}
  );
  const [addCare, { isLoading: isLoadingAddCare }] =
    useCreateCareTargetSystemMutation();
  const updateState = (action: string, data: any = null) => {
    dispatchAction({ type: action, data: data });
  };

  useEffect(() => {
    if (data) {
      updateState('model-changed', {
        ...data,
        organization: [data.owner],
      });
      if (data.owner) {
        setOrganization([data.owner]);
      }
    }
  }, [data]);

  let shouldShowCare = false;
  let orgCareCount: number | null = 0;
  if (organizations && organizations.length === 1) {
    const organization = organizations[0];
    if (!state.organization.length) {
      updateState('value-changed', { organization: [organization] });
    }
    orgCareCount = countCareSystems(targetSystems!, organization);
    shouldShowCare = isCareVisible(
      orgCareCount,
      mState,
      organizations[0],
      targetSystems
    );
  }

  let onSaveCare = () => {
    return addCare({
      apiBaseURL: config.apiBaseURL,
      org: organizations[0] || authUser.selectedOrgs[0],
    }).then((resp: any) => {
      // this is because state is invalid and in next step is not closed automaticaly
      updateState('reset');
      onChange();
      onClose();
      return resp;
    });
  };

  const { selectedAuthType: authType, legacy } = state;

  const isDemo = !!data && isTargetSystemFromDemoOrg(data, authUser);

  const defaultOnSave = () => {
    updateState('value-changed', { messages: true });
    return Promise.resolve();
  };

  const saveWithSelectedAuthType = () => {
    const ts = state.selectedAuthType.onSave(state);
    if (data) {
      ts.id = data.id;
      return updateTargetSystem({
        apiBaseURL: config.apiBaseURL,
        targetSystem: ts,
      });
    }
    const orgs =
      organizations && organizations.length > 0
        ? organizations
        : [authUser.selectedOrgs[0]];

    return saveTargetSystem({
      apiBaseURL: config.apiBaseURL,
      targetSystem: ts,
      orgs,
    });
  };

  const onSave = () => {
    if (shouldShowCare && isUseCare) {
      return onSaveCare();
    } else if (state.valid === true) {
      return saveWithSelectedAuthType();
    }

    return defaultOnSave();
  };

  const onCancel = () => {
    updateState('reset');
    if (onClose) {
      onClose();
    }
  };

  const formatMenu = apiFormats.map((item) => {
    return (
      <MenuItem key={item.key} value={item.value}>
        {item.description}
      </MenuItem>
    );
  });

  const {
    name,
    location,
    format,
    key,
    authEndpoint,
    grantType,
    username,
    password,
    updateKey,
    forceChange,
    redirectURI,
    code,
    privateKey,
    privateKeyPassword,
    allowIP,
  } = state;

  useEffect(() => {
    if (
      state.grantType !== 'authorization_code' &&
      (state.code !== '' || state.redirectURI !== '')
    ) {
      updateState('value-changed', { code: '', redirectURI: '' });
    }
    if (
      state.grantType !== 'jwt_bearer' &&
      (state.privateKey !== '' || state.privateKeyPassword !== '')
    ) {
      updateState('value-changed', { privateKey: '', privateKeyPassword: '' });
    }
  }, [
    state.grantType,
    state.code,
    state.redirectURI,
    state.privateKey,
    state.privateKeyPassword,
    state.name,
    state.allowIP,
  ]);

  const showHL7Warning =
    HL7Formats.indexOf(state.format) !== -1 &&
    UnsupportedHL7Devices.length &&
    UnsupportedHL7Types.length &&
    props.showWarning;

  const confirmationTitle = !!data ? 'Changing webhook' : 'Adding webhook';
  const confirmationQuestionText = !!data
    ? 'Are you sure that you want to make these changes?'
    : 'Are you sure that you want to add this webhook?';
  const isLegacy = !!state.format && legacy !== '';

  useMemo(() => {
    if (open && !data) {
      updateState('value-changed', { name: 'Default' });
    }
  }, [open, data]);

  const calculateQueryParams = (): any[] => {
    let fullURL: URL = {} as URL;
    try {
      fullURL = new URL(location);
    } catch (e) {} // cannot parse, let's ignore it

    const params = fullURL.searchParams || [];
    const model: any[] = [];
    let index = 0;
    params.forEach((value, key) => {
      if (value === '' && key === '') return;
      model.push(
        <>
          <Grid item md={6}>
            <TextField
              key={'query-' + index}
              placeholder="Key"
              label="Key"
              value={key}
              margin="dense"
              variant="outlined"
              className={classes.field}
              onChange={(e) => {
                if (params.has(key)) {
                  params.delete(key);
                }
                if (e.target.value === '') {
                  params.delete(key);
                } else {
                  params.set(e.target.value, '');
                }
                updateState('value-changed', { location: fullURL.toString() });
              }}
            ></TextField>
          </Grid>
          <Grid item md={6}>
            <TextField
              key={'query-' + (index + 1)}
              placeholder="Value"
              label="Value"
              value={value}
              margin="dense"
              variant="outlined"
              className={classes.field}
              onChange={(e) => {
                params.set(key, e.target.value);
                updateState('value-changed', { location: fullURL.toString() });
              }}
            ></TextField>
          </Grid>
        </>
      );
      index++;
    });
    model.push(
      <>
        <Grid item md={6}>
          <TextField
            key={'query-' + index}
            placeholder="Key"
            label="Key"
            margin="dense"
            variant="outlined"
            className={classes.field}
            onChange={(e) => {
              params.set(e.target.value, '');
              updateState('value-changed', { location: fullURL.toString() });
            }}
          ></TextField>
        </Grid>
        <Grid item md={6}>
          <TextField
            key={'query-' + (index + 1)}
            placeholder="Value"
            label="Value"
            margin="dense"
            variant="outlined"
            className={classes.field}
            onChange={(e) => {
              params.set(key, e.target.value);
              updateState('value-changed', { location: fullURL.toString() });
            }}
          ></TextField>
        </Grid>
      </>
    );
    return model;
  };

  // @ts-ignore
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {showHL7Warning ? (
            <Alert severity="warning">
              Configuration warning - Devices and/or measurements may not be
              supported wholly or partly
            </Alert>
          ) : null}
          <FormControl fullWidth margin="normal">
            <Visible when={shouldShowCare}>
              <List className={classes.careLabel}>
                <ListItem>
                  <ListItemText primary="Add Care as a webhook" />
                  <ListItemSecondaryAction>
                    <Switch
                      color="primary"
                      autoFocus={true}
                      checked={isUseCare}
                      onChange={(e, checked) => setUseCare(checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </Visible>
            <Visible when={userOrgs.length > 1 && !data}>
              <MedSelect
                name="organization"
                placeholder="Organization"
                error={isInvalid('organization', state)}
                value={organizations}
                MenuProps={{
                  style: { width: 0 },
                  getContentAnchorEl: null,
                }}
                fullWidth
                renderValue={(selected) => {
                  const orgs = (selected as string[]).map((orgId) => {
                    const org = (authUser &&
                      authUser.orgs &&
                      authUser.orgs.length &&
                      authUser.orgs.find((org) => org.id === orgId)) || {
                      name: '',
                    };
                    return org!.name;
                  });
                  return orgs.join(', ');
                }}
                multiple
                onChange={(e) => {
                  setOrganization(e.target.value as string[]);
                  updateState('value-changed', {
                    organization: e.target.value,
                  });
                }}
              >
                {userOrgs.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    <Checkbox checked={organizations.indexOf(org.id) > -1} />
                    <ListItemText primary={org.name} />
                  </MenuItem>
                ))}
              </MedSelect>
              {
                <FormHelperText
                  error={isInvalid('organization', state)}
                  className={classes.fieldText}
                >
                  {helpOrganization(
                    focusedFieldState,
                    isInvalid('organization', state)
                  )}
                </FormHelperText>
              }
            </Visible>
            <Collapse in={isUseCare === false || shouldShowCare === false}>
              <TextField
                id="name"
                key="name"
                label="Name"
                className={classes.field}
                inputProps={{ maxLength: 60 }}
                autoComplete="off"
                fullWidth
                required
                variant="outlined"
                margin="dense"
                error={isInvalid('name', state)}
                helperText={helpName(
                  focusedFieldState,
                  isInvalid('name', state)
                )}
                value={name}
                onChange={(e) => {
                  updateState('value-changed', { name: e.target.value });
                }}
                onFocus={() => dispatchFocusedField({ type: 'name' })}
              />

              {data ? (
                <TextField
                  id="targetSystemId"
                  key="targetSystemId"
                  className={classes.field}
                  value={data['id'] ? data['id'] : ''}
                  label="Webhook ID"
                  disabled
                  fullWidth={true}
                  margin="dense"
                  variant="outlined"
                />
              ) : null}

              {data ? (
                <TextField
                  id="organisation"
                  key="organisation"
                  className={classes.field}
                  value={data['owner'] ? data['owner'] : ''}
                  label="Organisation ID"
                  disabled
                  fullWidth={true}
                  margin="dense"
                  variant="outlined"
                />
              ) : null}
              <TextField
                id="url"
                key="url"
                className={classes.field}
                required={true}
                error={isInvalid('location', state)}
                helperText={helpLocation(
                  focusedFieldState,
                  isInvalid('location', state)
                )}
                value={stripHttpsFromTheBegining(location)}
                autoComplete="off"
                onChange={(e) => {
                  updateState('value-changed', {
                    location: appendHttpsOnTheBegining(e.target.value),
                  });
                }}
                placeholder="www.someurl.com"
                label="URL"
                fullWidth={true}
                margin="dense"
                variant="outlined"
                onFocus={() => dispatchFocusedField({ type: 'location' })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      style={{ marginRight: '0px' }}
                      position="start"
                    >
                      https://
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <IconButton
                      edge="end"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          appendHttpsOnTheBegining(location)
                        );
                        enqueueSnackbar('Copied to clipboard', {
                          variant: 'success',
                        });
                      }}
                    >
                      {' '}
                      <FileCopy></FileCopy>{' '}
                    </IconButton>
                  ),
                }}
              />

              <TextField
                id="allowIP"
                key="allowIP"
                label="Allow IP"
                className={classes.field}
                inputProps={{ maxLength: 60 }}
                autoComplete="off"
                fullWidth
                required
                variant="outlined"
                margin="dense"
                error={isInvalid('allowIP', state)}
                helperText={helpAllowIP(
                  focusedFieldState,
                  isInvalid('allowIP', state)
                )}
                placeholder="10.0.0.1 or CIDR notation 10.0.0.1/24"
                value={allowIP}
                onChange={(e) => {
                  updateState('value-changed', { allowIP: e.target.value });
                }}
                onFocus={() => dispatchFocusedField({ type: 'allowIP' })}
              />

              <Grid container>
                <Grid item md={12}>
                  <small>HTTP Query Params</small>
                </Grid>
                {calculateQueryParams()}
              </Grid>
              <>
                <MedSelect
                  name="format"
                  placeholder="Standard and Format"
                  value={format}
                  disabled={isLegacy}
                  onFocus={() => dispatchFocusedField({ type: 'format' })}
                  error={isInvalid('format', state)}
                  onChange={(e) => {
                    if (e.target.value) {
                      updateState('value-changed', { format: e.target.value });
                    }
                  }}
                >
                  {formatMenu}
                  {isLegacy && (
                    <MenuItem key={legacy} value={legacy}>
                      {legacy}
                    </MenuItem>
                  )}
                </MedSelect>
                {
                  <FormHelperText
                    error={isInvalid('format', state)}
                    className={classes.fieldText}
                  >
                    {helpFormat(focusedFieldState, isInvalid('format', state))}
                  </FormHelperText>
                }
              </>
              <MedSelect
                name="authentication-type"
                value={authType ? authType.type : ''}
                renderValue={(value) => authTypes[value as string].description}
                options={orderedAuthTypes}
                placeholder="Authentication type"
                error={isInvalid('selectedAuthType', state)}
                disabled={isLegacy}
                valueGetter={(t) => t.type}
                optionTextGetter={(t) => t.description}
                onFocus={() =>
                  dispatchFocusedField({ type: 'selectedAuthType' })
                }
                onChange={(e) => {
                  if (e.target.value) {
                    const key = e.target.value as string;
                    updateState('value-changed', {
                      selectedAuthType: authTypes[key],
                      updateKey: true,
                      forceChange: true,
                      authEndpoint: '',
                      username: '',
                      password: '',
                      messages: false,
                    });
                  }
                }}
              />
              {
                <FormHelperText
                  error={isInvalid('selectedAuthType', state)}
                  className={classes.fieldText}
                >
                  {helpSelectedAuthType(
                    focusedFieldState,
                    isInvalid('selectedAuthType', state)
                  )}
                </FormHelperText>
              }

              {authType ? (
                <>
                  <Collapse in={authType.type === 'apikey'}>
                    <Collapse in={data === undefined || updateKey === true}>
                      <TextField
                        id="api-key"
                        className={classes.field}
                        required={true}
                        error={isInvalid('key', state)}
                        onFocus={() => dispatchFocusedField({ type: 'key' })}
                        helperText={helpKey(
                          focusedFieldState,
                          isInvalid('key', state)
                        )}
                        autoComplete="off"
                        value={key}
                        onChange={(e) => {
                          updateState('value-changed', { key: e.target.value });
                        }}
                        label="API key"
                        fullWidth={true}
                        margin="dense"
                        variant="outlined"
                        type={showPassword ? 'text' : 'password'}
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showPassword}
                            onChange={() => {
                              setShowPassword(!showPassword);
                            }}
                            name="showpassword"
                          />
                        }
                        label="Show password"
                      />
                    </Collapse>
                    <Display visible={data != null && forceChange === false}>
                      <FormControlLabel
                        label="Edit API key"
                        className={classes.field}
                        control={
                          <Checkbox
                            checked={updateKey}
                            onChange={(e) =>
                              updateState('value-changed', {
                                updateKey: e.target.checked,
                              })
                            }
                          />
                        }
                      />
                    </Display>
                  </Collapse>

                  <Collapse
                    in={authType.type === 'basic' || authType.type === 'digest'}
                  >
                    <TextField
                      className={classes.field}
                      required={true}
                      error={isInvalid('username', state)}
                      onFocus={() => dispatchFocusedField({ type: 'username' })}
                      helperText={helpUsername(
                        focusedFieldState,
                        isInvalid('username', state)
                      )}
                      value={username}
                      onChange={(e) => {
                        updateState('value-changed', {
                          username: e.target.value,
                        });
                      }}
                      placeholder="Username"
                      label="Username"
                      fullWidth={true}
                      margin="dense"
                      variant="outlined"
                    />
                    <Collapse in={data === undefined || updateKey === true}>
                      <TextField
                        type={showPassword ? 'text' : 'password'}
                        className={classes.field}
                        required={true}
                        autoComplete="off"
                        error={isInvalid('password', state)}
                        onFocus={() =>
                          dispatchFocusedField({ type: 'password' })
                        }
                        helperText={helpPassword(
                          focusedFieldState,
                          isInvalid('password', state)
                        )}
                        value={password}
                        onChange={(e) => {
                          updateState('value-changed', {
                            password: e.target.value,
                          });
                        }}
                        label="Password"
                        fullWidth={true}
                        margin="dense"
                        variant="outlined"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showPassword}
                            onChange={() => {
                              setShowPassword(!showPassword);
                            }}
                            name="showpassword"
                          />
                        }
                        label="Show password"
                      />
                    </Collapse>
                    <Display visible={data != null && forceChange === false}>
                      <FormControlLabel
                        label="Edit password"
                        className={classes.field}
                        control={
                          <Checkbox
                            checked={updateKey}
                            onChange={(e) =>
                              updateState('value-changed', {
                                updateKey: e.target.checked,
                              })
                            }
                          />
                        }
                      />
                    </Display>
                  </Collapse>

                  <Collapse in={authType.type === 'oauth'}>
                    <TextField
                      id="authEndpoint"
                      className={classes.field}
                      required={true}
                      error={isInvalid('authEndpoint', state)}
                      helperText={helpAuthURL(
                        focusedFieldState,
                        isInvalid('authEndpoint', state)
                      )}
                      onFocus={() =>
                        dispatchFocusedField({ type: 'authEndpoint' })
                      }
                      value={stripHttpsFromTheBegining(authEndpoint)}
                      onChange={(e) => {
                        updateState('value-changed', {
                          authEndpoint: appendHttpsOnTheBegining(
                            e.target.value
                          ),
                        });
                      }}
                      placeholder="www.someurl.com"
                      autoComplete="off"
                      label="Auth URL"
                      fullWidth={true}
                      margin="dense"
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment
                            style={{ marginRight: '0px' }}
                            position="start"
                          >
                            https://
                          </InputAdornment>
                        ),
                      }}
                    />

                    <MedSelect
                      name="grantType"
                      placeholder="Grant Type"
                      value={grantType}
                      error={isInvalid('grantType', state)}
                      options={grantTypes}
                      valueGetter={(gt) => gt.value}
                      optionTextGetter={(gt) => gt.description}
                      onFocus={() =>
                        dispatchFocusedField({ type: 'grantType' })
                      }
                      onChange={(e) => {
                        if (e.target.value) {
                          updateState('value-changed', {
                            grantType: e.target.value,
                          });
                        }
                      }}
                    />
                    {
                      <FormHelperText
                        error={isInvalid('grantType', state)}
                        className={classes.fieldText}
                      >
                        {helpGrantType(
                          focusedFieldState,
                          isInvalid('grantType', state)
                        )}
                      </FormHelperText>
                    }

                    {grantType === 'authorization_code' && (
                      <>
                        <TextField
                          id="code"
                          className={classes.field}
                          required={true}
                          error={isInvalid('code', state)}
                          onFocus={() => dispatchFocusedField({ type: 'code' })}
                          helperText={helpCode(
                            focusedFieldState,
                            isInvalid('code', state)
                          )}
                          value={code}
                          onChange={(e) => {
                            updateState('value-changed', {
                              code: e.target.value,
                            });
                          }}
                          placeholder="Code"
                          label="Code"
                          fullWidth={true}
                          margin="dense"
                          variant="outlined"
                        />

                        <TextField
                          id="redirectURI"
                          className={classes.field}
                          required={false}
                          autoComplete="off"
                          error={isInvalid('redirectURI', state)}
                          onFocus={() =>
                            dispatchFocusedField({ type: 'redirectURI' })
                          }
                          helperText={helpRedirectURI(
                            focusedFieldState,
                            isInvalid('redirectURI', state)
                          )}
                          value={stripHttpsFromTheBegining(
                            redirectURI ? redirectURI : ''
                          )}
                          onChange={(e) => {
                            updateState('value-changed', {
                              redirectURI: e.target.value
                                ? appendHttpsOnTheBegining(e.target.value)
                                : '',
                            });
                          }}
                          placeholder="www.someurl.com"
                          label="Redirect URI"
                          fullWidth={true}
                          margin="dense"
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment
                                style={{ marginRight: '0px' }}
                                position="start"
                              >
                                https://
                              </InputAdornment>
                            ),
                          }}
                        />
                      </>
                    )}

                    <TextField
                      id="auth-username"
                      className={classes.field}
                      required={true}
                      error={isInvalid('username', state)}
                      onFocus={() => dispatchFocusedField({ type: 'username' })}
                      helperText={helpUsername(
                        focusedFieldState,
                        isInvalid('username', state)
                      )}
                      value={username}
                      onChange={(e) => {
                        updateState('value-changed', {
                          username: e.target.value,
                        });
                      }}
                      label={
                        grantType === 'jwt_bearer' ? 'Client ID' : 'Username'
                      }
                      fullWidth={true}
                      margin="dense"
                      variant="outlined"
                    />
                    <Collapse in={data === undefined || updateKey === true}>
                      <TextField
                        type={showPassword ? 'text' : 'password'}
                        id="auth-password"
                        className={classes.field}
                        autoComplete="off"
                        required
                        fullWidth
                        error={isInvalid('password', state)}
                        onFocus={() =>
                          dispatchFocusedField({ type: 'password' })
                        }
                        helperText={helpPassword(
                          focusedFieldState,
                          isInvalid('password', state)
                        )}
                        value={password}
                        onChange={(e) => {
                          updateState('value-changed', {
                            password: e.target.value,
                          });
                        }}
                        label={
                          grantType === 'jwt_bearer'
                            ? 'Client secret'
                            : 'Password'
                        }
                        margin="dense"
                        variant="outlined"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showPassword}
                            onChange={() => {
                              setShowPassword(!showPassword);
                            }}
                            name="showpassword"
                          />
                        }
                        label="Show password"
                      />
                      {grantType === 'jwt_bearer' && (
                        <>
                          <TextField
                            id="private_key"
                            className={classes.field}
                            required={true}
                            type={showPrivateKey ? 'text' : 'password'}
                            error={isInvalid('privateKey', state)}
                            onFocus={() =>
                              dispatchFocusedField({ type: 'privateKey' })
                            }
                            helperText={helpPrivateKey(
                              focusedFieldState,
                              isInvalid('privateKey', state)
                            )}
                            value={privateKey}
                            onChange={(e) => {
                              updateState('value-changed', {
                                privateKey: e.target.value,
                              });
                            }}
                            placeholder="Private key"
                            label="Private key"
                            fullWidth
                            margin="dense"
                            variant="outlined"
                            {...(showPrivateKey ? { multiline: true } : {})}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showPrivateKey}
                                onChange={() => {
                                  setShowPrivateKey(!showPrivateKey);
                                }}
                                name="showPrivateKey"
                                color="primary"
                              />
                            }
                            label="Show private key"
                          />

                          {isPrivateKeyEncrypted(privateKey) ? (
                            <>
                              <TextField
                                id="password_private_key"
                                className={classes.field}
                                required={false}
                                error={isInvalid('privateKeyPassword', state)}
                                onFocus={() =>
                                  dispatchFocusedField({
                                    type: 'privateKeyPassword',
                                  })
                                }
                                helperText={helpPrivateKeyPassword(
                                  focusedFieldState,
                                  isInvalid('privateKeyPassword', state)
                                )}
                                value={privateKeyPassword}
                                onChange={(e) => {
                                  updateState('value-changed', {
                                    privateKeyPassword: e.target.value,
                                  });
                                }}
                                placeholder="Private key password"
                                label="Private key password"
                                fullWidth={true}
                                margin="dense"
                                variant="outlined"
                                type={
                                  showPrivateKeyPassword ? 'text' : 'password'
                                }
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={showPrivateKeyPassword}
                                    onChange={() => {
                                      setShowPrivateKeyPassword(
                                        !showPrivateKeyPassword
                                      );
                                    }}
                                    name="showPrivateKey"
                                    color="primary"
                                  />
                                }
                                label="Show private key password"
                              />
                            </>
                          ) : null}
                        </>
                      )}
                    </Collapse>

                    <Display visible={data != null && forceChange === false}>
                      <FormControlLabel
                        className={classes.field}
                        label={
                          grantType === 'jwt_bearer'
                            ? 'Edit secrets'
                            : 'Edit password'
                        }
                        control={
                          <Checkbox
                            checked={updateKey}
                            onChange={(e) =>
                              updateState('value-changed', {
                                updateKey: e.target.checked,
                              })
                            }
                          />
                        }
                      />
                    </Display>
                  </Collapse>

                  <Collapse in={authType.type === 'hmacsha256'}>
                    <TextField
                      className={classes.field}
                      value={username}
                      placeholder="Secret Key"
                      label="Secret Key"
                      helperText={
                        username
                          ? 'All request will be signed with this secret key'
                          : 'Secret key will be generated once you create webhook'
                      }
                      fullWidth={true}
                      margin="dense"
                      variant="outlined"
                      disabled
                      type={showPassword ? 'text' : 'password'}
                    />
                    {username && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showPassword}
                            onChange={() => {
                              setShowPassword(!showPassword);
                            }}
                            name="showpassword"
                          />
                        }
                        label="Show password"
                      />
                    )}
                  </Collapse>
                </>
              ) : null}
            </Collapse>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={onCancel}>
            Cancel
          </Button>
          <ButtonProgress
            isLoading={isLoading || isLoadingAddCare}
            variant="contained"
            color="primary"
            disabled={isDemo}
            onClick={() => {
              setConfirmOpen(true);
            }}
          >
            Save
          </ButtonProgress>
        </DialogActions>
      </Dialog>

      <ConfirmationAction
        isOpen={confirmOpen}
        title={confirmationTitle}
        confirmationText="Yes"
        cancelText="No"
        onConfirm={() => {
          onSave().then((resp: any) => {
            if (!resp) {
              return;
            }
            if (resp.error) {
              enqueueSnackbar(parseErrorMessage(resp.error), {
                variant: 'error',
              });
              return;
            }

            if (state.valid === true) {
              updateState('reset');
              onChange(resp.data.warnMessage);
              onClose();
            }
          });
        }}
        maxWidth={showHL7Warning ? 'md' : 'sm'}
        onClose={() => {
          setConfirmOpen(false);
        }}
      >
        {confirmationQuestionText}
        {showHL7Warning ? (
          <Alert severity="warning" style={{ marginTop: '1em' }}>
            Not all devices and/or measurement types are supported, including
            (but not limited to):
            <div style={{ marginTop: '1em' }}>
              {UnsupportedHL7Types.length ? (
                <>
                  <strong>Measurement Types:</strong>
                  <ul>
                    {UnsupportedHL7Types.map((type) => (
                      <li key={type}>{type}</li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
          </Alert>
        ) : null}
      </ConfirmationAction>
    </>
  );
};

export default EditTargetSystemDialog;
